import {
  inject,
  Component,
  ViewChild,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { UserAccessLevel } from 'src/app/core/services/user.service';
import { UtilsService } from 'src/app/core/services/utils.service';

import { DashboardSettingsComponent } from '../../components/dashboard-settings/dashboard-settings.component';
import { AuditTrailComponent } from '../../../shared/components/audit-trail/audit-trail.component';
import { OutputLogsComponent } from '../../../shared/components/output-logs/output-logs.component';
import { ObjectPermissionsComponent } from '../../../objects/components/object-permissions/object-permissions.component';
import { DashboardPresentationSettingsComponent } from './dashboard-presentation-settings/dashboard-presentation-settings.component';

import { Dashboard } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';

import {
  injectCreateDashboardMutation,
  injectUpdateDashboardMutation,
} from 'src/app/api/queries/dashboard.queries';

import { AppState } from 'src/app/core/state/app.state';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectUser } from 'src/app/core/state';
import { tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface DashboardDialogData extends TabDialogDataBase {
  dashboard?: Dashboard;
}

/** Close data: the submitted form plus `saved`, the in-flight request (SavedCloseData). */
interface DashboardDialogResult {
  saved?: Promise<unknown>;
  [field: string]: unknown;
}

@Component({
  selector: 'app-dashboard-editor-dialog',
  templateUrl: './dashboard-editor-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard-editor-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class DashboardEditorDialogComponent
  extends TabDialogBase<DashboardDialogData, DashboardDialogResult>
  implements SearchableDialog
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private utilsService = inject(UtilsService);

  private createDashboardMutation = injectCreateDashboardMutation();
  private updateDashboardMutation = injectUpdateDashboardMutation();

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  objectActionTitle = '';
  objectNameTitle = '';
  objectIcon: IconProp = ['fas', 'gauge-high'];
  objectActionIcon: IconProp = ['fas', 'cog'];
  // * store presentation data from DashboardPresentationSettingsComponent
  private presentationData: {
    presentation_settings?: any;
    pages?: any[];
  } = {};

  get isUpdateMode(): boolean {
    return !!this.dialogData.dashboard;
  }

  get dashboard(): Dashboard | undefined {
    return this.dialogData.dashboard;
  }

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  private setupUIState(): void {
    if (this.isUpdateMode && this.dashboard) {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.edit-dashboard-settings',
      );
      this.objectNameTitle = this.dashboard.title;
      this.objectIcon = getIconForDataObject(
        this.dashboard.object_class_id,
        this.dashboard.object_type_id,
      );
    } else {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.create-dashboard',
      );
      this.objectNameTitle = this.translateService.instant(
        'popup.object-title.new-dashboard',
      );
      this.objectIcon = ['fas', 'gauge-high'];
    }
  }

  setupTabs(): void {
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant(
          'dashboard-editor-popup.tab.general',
        ),
        component: DashboardSettingsComponent,
        inputs: {
          dashboard: this.dashboard,
          searchString$: this.searchString$,
        },
      },
    ];

    if (this.dashboard) {
      this.tabs.push(
        {
          id: 'presentation',
          label: this.translateService.instant(
            'dashboard-editor-popup.tab.presentation',
          ),
          component: DashboardPresentationSettingsComponent,
          // ! Manages its own data structure via getPresentationData() - exclude from combineValues()
          excludeFromValues: true,
          inputs: {
            dashboard: this.dashboard,
          },
        },
        {
          id: 'permissions',
          label: this.translateService.instant(
            'dashboard-editor-popup.button.permissions',
          ),
          component: ObjectPermissionsComponent,
          inputs: {
            searchString$: this.searchString$,
            objects: [this.dashboard],
            companyId: this.dashboard.company_id,
            allowOwnerChange: true,
          },
          objectPermissions: ['can_manage'],
        },
        {
          id: 'audit-trail',
          label: this.translateService.instant(
            'dashboard-editor.tab.audit-trail',
          ),
          component: AuditTrailComponent,
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          inputs: {
            searchString$: this.searchString$,
            object: this.dashboard,
            hasOwner: true,
            timeCreatedKey: 'time_created.absolute',
            timeModifiedKey: 'time_modified.absolute',
          },
          objectPermissions: ['can_manage'],
        },
        {
          id: 'output-logs',
          label: this.translateService.instant(
            'dashboard-editor.tab.output-logs',
          ),
          component: OutputLogsComponent,
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          inputs: {
            object: this.dashboard,
          },
        },
      );
      this.store
        .select(selectUser)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((user) =>
            this.filterTabsByObjectPermissions(this.dashboard, user),
          ),
        )
        .subscribe();
    }
  }

  protected override performSave(
    formData: any,
  ): DialogCloseOptions<DashboardDialogResult> {
    this.storePresentationData();

    // * dotted i18n control names (title_i18n.N) must become a nested title_i18n
    // * object before hitting the wire - PHP mangles dots in param names
    const allFormData = {
      ...this.utilsService.convertFormDataI18nKeys(formData),
      workspace_id: formData['workspace.id'],
      ...this.presentationData,
    };

    delete allFormData['workspace.id'];
    // * optimistic close; the hook's onSuccess owns the cache patch and broadcast,
    // * so nothing here depends on the dialog living
    if (this.isUpdateMode && this.dashboard) {
      const saved = this.updateDashboardMutation.mutateAsync({
        id: this.dashboard.id,
        dashboard: allFormData,
      });
      saved.catch(() => undefined);
      return {
        reason: DialogCloseReason.Update,
        data: { ...allFormData, saved },
      };
    } else {
      const saved = this.createDashboardMutation.mutateAsync(allFormData);
      saved.catch(() => undefined);
      return {
        reason: DialogCloseReason.Save,
        data: { ...allFormData, saved },
      };
    }
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    // * all valid, proceed with save
    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({ reason: DialogCloseReason.Cancel });
  }

  private storePresentationData(): void {
    const presentationTab = this.tabs.find(
      (tab) => tab.component === DashboardPresentationSettingsComponent,
    );
    if (!presentationTab) return;

    const presentationComponent = this.tabManager.getTabComponent(
      presentationTab.id,
    ) as DashboardPresentationSettingsComponent;
    const presentationFg = this.tabManager.getTabFormGroup(presentationTab.id);

    if (!presentationComponent || !presentationFg) {
      console.warn('Presentation tab not initialized yet');
      return;
    }

    const shouldSave = presentationFg.dirty;

    if (shouldSave) {
      const currentData = presentationComponent.getPresentationData();

      this.presentationData = {
        presentation_settings: currentData.presentation_settings,
        pages: currentData.pages,
      };

      console.log(
        '%c presentationData:',
        'color: cyan; font-weight: bold;',
        this.presentationData,
      );
    }
  }
}
